import { useEffect, useMemo, useRef, useState } from 'react';
import { Save, AlertTriangle } from '@/components/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { CategoryItemPicker } from '@/components/form/CategoryItemPicker';
import { Textarea } from '@/components/form/Textarea';
import { ItemsTable } from '@/components/form/ItemsTable';
import { useToast } from '@/components/ui/useToast';
import { useAuth } from '@/auth/useAuth';
import {
  createEnquiry,
  updateEnquiry,
  type CreateEnquiryPayload,
  type Enquiry,
  type Priority,
} from '@/api/enquiry';
import { listParties, createParty, type Party } from '@/api/parties';
import { listCatalogue, type CatalogueRow } from '@/api/catalogue';
import { formatINR, todayISO } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { useFieldErrors } from '@/lib/useFieldErrors';
import { listHsnGstBands } from '@/api/hsnGst';
import { gstAddedOnTop, gstSplit, inclusiveFromExRate } from '@/lib/gstSplit';

interface Props {
  open: boolean;
  editing: Enquiry | null;
  presetItemId?: number | null;
  presetCustomerType?: string | null;
  presetCartItems?: { itemId: number; qty: number }[];
  onClose: () => void;
  onSaved?: () => void;
}

interface LineState {
  itemId: number | null;
  itemCode: string;
  itemName: string;
  hsn: string;
  gstPct: string;
  qty: string;
  unit: string;
  rate: string;
  // Custom per-line discount (Corporate & Projects only): a value + whether it
  // is a percentage or a flat rupee amount.
  discountMode: '%' | '₹';
  discount: string;
  stockStatus?: 'GREEN' | 'AMBER' | 'RED' | null;
  currentStock?: number | null;
}

type FormState = {
  enqDate: string;
  partyId: number | '';
  deliveryAddress: string;
  customerPoRef: string;
  expectedDelivery: string;
  priority: Priority;
  salesRep: string;
  source: string;
  remarks: string;
  gstOnRatePc: boolean;
  specialDiscType: 'amount' | 'pct';
  specialDiscValue: string;
  specialDiscLabel: string;
  items: LineState[];
};

function emptyForm(): FormState {
  return {
    enqDate: todayISO(),
    partyId: '',
    deliveryAddress: '',
    customerPoRef: '',
    expectedDelivery: '',
    priority: 'Normal',
    salesRep: '',
    source: 'Direct',
    remarks: '',
    gstOnRatePc: false,
    specialDiscType: 'amount',
    specialDiscValue: '',
    specialDiscLabel: 'CD',
    items: [],
  };
}

function emptyLine(): LineState {
  return {
    itemId: null,
    itemCode: '',
    itemName: '',
    hsn: '',
    gstPct: '12',
    qty: '',
    unit: '',
    rate: '',
    discountMode: '%',
    discount: '',
  };
}

function fromEnquiry(e: Enquiry): FormState {
  return {
    enqDate: e.enqDate.slice(0, 10),
    partyId: e.partyId,
    deliveryAddress: e.deliveryAddress ?? '',
    customerPoRef: e.customerPoRef ?? '',
    expectedDelivery: e.expectedDelivery ? e.expectedDelivery.slice(0, 10) : '',
    priority: e.priority,
    salesRep: e.salesRep,
    source: e.source ?? '',
    remarks: e.remarks ?? '',
    gstOnRatePc: e.gstOnRatePc ?? false,
    specialDiscType: (e.specialDiscountType ?? 'amount') as 'amount' | 'pct',
    specialDiscValue: Number(e.specialDiscount) > 0 ? String(e.specialDiscount) : '',
    specialDiscLabel: e.specialDiscountLabel ?? 'CD',
    items: e.items.map((it) => ({
      itemId: it.itemId,
      itemCode: it.itemCode ?? '',
      itemName: it.itemName,
      hsn: it.hsn ?? '',
      gstPct: it.gstPct,
      qty: it.qty,
      unit: it.unit,
      rate: it.rate,
      discountMode: (it.discountAmt != null ? '₹' : '%') as '%' | '₹',
      discount: it.discountAmt != null ? it.discountAmt : it.discountPct != null ? it.discountPct : '',
      stockStatus: it.stockStatus,
    })),
  };
}

const emptyLead = () => ({
  contactPerson: '',
  companyName: '',
  mobile: '',
  email: '',
  address: '',
});

export function EnquiryFormModal({ open, editing, presetItemId, presetCustomerType, presetCartItems, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const fe = useFieldErrors<'company' | 'phone' | 'salesRep'>();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState(emptyLead);
  const presetSeeded = useRef(false);

  useEffect(() => {
    if (!open) { presetSeeded.current = false; setLeadOpen(false); setLead(emptyLead()); return; }
    presetSeeded.current = false;
    if (editing) {
      setForm(fromEnquiry(editing));
    } else {
      // Prefill Sales Rep with the logged-in user's name — the person
      // capturing the enquiry is almost always the sales rep on the call.
      const base = emptyForm();
      setForm({ ...base, salesRep: user?.fullName ?? '' });
    }
  }, [open, editing, user]);

  // Customers + catalogue dataset for in-modal item picker.
  const { data: customersResp } = useQuery({
    queryKey: ['parties', { type: 'customer', includeLeads: true, pageSize: 10000 }],
    queryFn: () => listParties({ type: 'customer', includeLeads: true, pageSize: 10000 }),
    enabled: open,
    staleTime: 60_000,
  });
  const allCustomers: Party[] = useMemo(() => customersResp?.data ?? [], [customersResp]);
  const customers = useMemo(
    () => presetCustomerType ? allCustomers.filter((c) => c.customerType === presetCustomerType) : allCustomers,
    [allCustomers, presetCustomerType],
  );

  const customerType = useMemo(() => {
    const id = form.partyId;
    if (typeof id !== 'number') return undefined;
    return allCustomers.find((c) => c.id === id)?.customerType ?? undefined;
  }, [form.partyId, allCustomers]);
  // Custom per-line discounts are only offered for Corporate & Projects.
  const isCorporate = customerType === 'Corporate & Projects';
  // Corporate & Projects + "GST Extra on Rate/PC": the discounted rate is ex-GST
  // and GST is added on top. Everyone else: MRP already includes GST.
  const gstOnTop = gstAddedOnTop(form.gstOnRatePc, customerType);
  // Net line value after the custom discount (for the live preview).
  const lineNet = (row: LineState) => {
    const gross = Number(row.qty || 0) * Number(row.rate || 0);
    const d = Number(row.discount || 0);
    if (!isCorporate || !d || d <= 0) return gross;
    return row.discountMode === '₹'
      ? Math.max(0, gross - d)
      : gross * (1 - Math.min(d, 100) / 100);
  };

  // HSN GST master bands — each line's GST% is picked from these by price, the
  // same way the Sales Order picks it.
  const { data: gstBands } = useQuery({
    queryKey: ['hsn-gst', 'bands'],
    queryFn: listHsnGstBands,
    enabled: open,
    staleTime: 60_000,
  });

  // Load the full catalogue once a customer is chosen — customer type drives
  // the price/discount context shown on the order.
  const { data: catalogueRows } = useQuery({
    queryKey: ['catalogue', { customerType, full: true }],
    queryFn: () => listCatalogue({ customerType, full: true }),
    enabled: open && (typeof form.partyId === 'number' || !!presetItemId || (presetCartItems?.length ?? 0) > 0),
    staleTime: 0,
  });
  const catalogue: CatalogueRow[] = useMemo(() => catalogueRows ?? [], [catalogueRows]);

  // When opened with preset item (from Catalogue +Add button), seed it exactly once.
  useEffect(() => {
    if (!open || !presetItemId || catalogue.length === 0 || presetSeeded.current) return;
    const it = catalogue.find((c) => c.id === presetItemId);
    if (!it) return;
    presetSeeded.current = true;
    const filled: LineState = {
      itemId: it.id,
      itemCode: it.code,
      itemName: it.name,
      hsn: it.hsn ?? '',
      gstPct: String(it.gstPct),
      qty: '1',
      unit: it.unit,
      rate: String(it.customerPrice),
      discountMode: '%',
      discount: '',
      stockStatus: it.stockStatus,
      currentStock: it.currentStock,
    };
    setForm((s) => ({ ...s, items: [...s.items, filled] }));
  }, [open, presetItemId, catalogue]);

  // When opened from the shopping cart, seed every cart line exactly once.
  useEffect(() => {
    if (!open || !presetCartItems || presetCartItems.length === 0 || catalogue.length === 0 || presetSeeded.current) return;
    const filled: LineState[] = [];
    for (const cartLine of presetCartItems) {
      const it = catalogue.find((c) => c.id === cartLine.itemId);
      if (!it) continue;
      filled.push({
        itemId: it.id,
        itemCode: it.code,
        itemName: it.name,
        hsn: it.hsn ?? '',
        gstPct: String(it.gstPct),
        qty: String(cartLine.qty),
        unit: it.unit,
        rate: String(it.customerPrice),
        discountMode: '%',
        discount: '',
        stockStatus: it.stockStatus,
        currentStock: it.currentStock,
      });
    }
    if (filled.length > 0) {
      presetSeeded.current = true;
      setForm((s) => ({ ...s, items: filled }));
    }
  }, [open, presetCartItems, catalogue]);

  // When customer type changes (catalogue refreshes), re-resolve rates for all selected lines.
  useEffect(() => {
    if (!catalogue.length) return;
    setForm((s) => ({
      ...s,
      items: s.items.map((line) => {
        if (!line.itemId) return line;
        const it = catalogue.find((c) => c.id === line.itemId);
        if (!it) return line;
        return { ...line, rate: String(it.customerPrice) };
      }),
    }));
  }, [catalogue]);



  const totals = useMemo(() => {
    let total = 0;
    let shortLines = 0;
    for (const l of form.items) {
      const q = Number(l.qty || 0);
      const r = Number(l.rate || 0);
      const gross = q * r;
      const d = Number(l.discount || 0);
      const net =
        isCorporate && d > 0
          ? l.discountMode === '₹'
            ? Math.max(0, gross - d)
            : gross * (1 - Math.min(d, 100) / 100)
          : gross;
      // Rate is GST-inclusive: the discounted net is the billed amount — unless
      // GST is added on top (Corporate & Projects with the tick).
      total += gstOnTop ? gstSplit(net, q, l.hsn, Number(l.gstPct || 0), gstBands, true).amount : net;
      if (l.stockStatus === 'RED') shortLines++;
    }
    return { total: Math.round(total * 100) / 100, shortLines };
  }, [form.items, isCorporate, gstOnTop, gstBands]);

  const selectedParty = useMemo(
    () => customers.find((c) => c.id === form.partyId) ?? null,
    [customers, form.partyId],
  );

  const editingId = editing?.id ?? null;
  const isReadOnly = editing && editing.stage !== 'new' && editing.stage !== 'reviewed';

  const leadMutation = useMutation({
    mutationFn: () => {
      const companyName = lead.companyName.trim();
      const contactPerson = lead.contactPerson.trim();
      const mobile = lead.mobile.trim();
      const email = lead.email.trim();
      const addressLine1 = lead.address.trim();
      return createParty({
        name: companyName,
        contactPerson: contactPerson || undefined,
        mobile: mobile || undefined,
        whatsapp: mobile || undefined,
        email: email || undefined,
        addressLine1: addressLine1 || undefined,
        isCustomer: true,
        customerType: 'Lead',
      });
    },
    onSuccess: (newParty) => {
      qc.invalidateQueries({ queryKey: ['parties'] });
      toast.push(`Lead added: ${newParty.name}`, 'success');
      setForm({
        ...form,
        partyId: newParty.id,
        deliveryAddress:
          form.deliveryAddress.trim() === ''
            ? [newParty.addressLine1, newParty.addressLine2, newParty.city, newParty.stateCode, newParty.pin]
                .filter(Boolean)
                .join(', ')
            : form.deliveryAddress,
      });
      setLead(emptyLead());
      setLeadOpen(false);
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Could not save lead'), 'error');
    },
  });

  function onSaveLead() {
    if (!fe.validate({
      company: !lead.companyName.trim() && 'Company name is required',
      phone: lead.mobile.trim() && !/^\d{10}$/.test(lead.mobile.trim()) && 'Phone must be 10 digits',
    })) return;
    leadMutation.mutate();
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = toPayload(form);
      return editingId ? updateEnquiry(editingId, payload) : createEnquiry(payload);
    },
    onSuccess: (enq) => {
      qc.invalidateQueries({ queryKey: ['enquiries'] });
      qc.invalidateQueries({ queryKey: ['mrn'] });
      if (editingId) {
        toast.push(`Enquiry ${enq.enqNo} updated`, 'success');
      } else {
        toast.pushStep(`Enquiry ${enq.enqNo} created`, {
          body: 'Next step — send quotation, or promote to Order Review.',
          href: '/sales/enq',
          nextLabel: 'Open Enquiries',
          tag: `enq-created-${enq.id}`,
        });
      }
      onSaved?.();
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Save failed'), 'error');
    },
  });

  function onSubmit() {
    if (!form.partyId) {
      toast.push('Select a customer', 'error');
      return;
    }
    if (!fe.validate({ salesRep: !form.salesRep.trim() && 'Sales Rep is required' })) return;
    // Items are optional — a "general enquiry" (customer walk-in, phone
    // query with no specific SKU yet) is a valid save state. Downstream
    // Order Review will prompt the rep to add items before generating an
    // order, so nothing later breaks on an empty items array.
    saveMutation.mutate();
  }

  return (
    <Modal
      size="2xl"
      open={open}
      onClose={onClose}
      title={editingId ? `Edit Enquiry — ${editing?.enqNo}` : 'New Enquiry'}
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={saveMutation.isPending || !!isReadOnly}
          >
            {saveMutation.isPending ? 'Saving…' : (<span className="inline-flex items-center gap-1"><Save size={14} /> Save Enquiry</span>)}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <FormGroup label="ENQ Number">
          <Input value={editing?.enqNo ?? 'Auto'} readOnly className="opacity-70" />
        </FormGroup>
        <FormGroup label="Date" required>
          <Input
            type="date"
            value={form.enqDate}
            onChange={(e) => setForm({ ...form, enqDate: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Expected Delivery">
          <Input
            type="date"
            value={form.expectedDelivery}
            onChange={(e) => setForm({ ...form, expectedDelivery: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Customer" required className="lg:col-span-2">
          <div className="flex items-center gap-1.5">
            <SearchableSelect
              className="flex-1"
              value={form.partyId}
              options={customers.map<SearchableOption>((c) => ({
                value: c.id,
                label: c.name,
                sublabel: c.customerType === 'Lead' ? undefined : (c.customerType ?? undefined),
                badge: c.customerType === 'Lead' ? { text: 'Lead', tone: 'warning' } : undefined,
              }))}
              placeholder="Type to search customers…"
              disabled={!!isReadOnly}
              onChange={(v) => {
                const partyId = typeof v === 'number' ? v : '';
                const p = partyId ? customers.find((c) => c.id === partyId) : undefined;
                // Delivery address always follows the selected customer's master
                // address. (Edit it afterwards if a one-off shipping address is needed.)
                const newAutoAddr = p
                  ? [p.addressLine1, p.addressLine2, p.city, p.stateCode, p.pin]
                      .filter(Boolean).join(', ')
                  : '';
                setForm({ ...form, partyId, deliveryAddress: newAutoAddr });
              }}
            />
            {/* Inline lead creation is hidden — leads are created in the Lead
                Dashboard now. Remove `hidden` to restore this shortcut. */}
            <button
              type="button"
              onClick={() => setLeadOpen((x) => !x)}
              disabled={!!isReadOnly}
              className="hidden h-9 items-center gap-1 flex-shrink-0 rounded-md border border-accent/30 bg-accent/10 px-2 text-xs font-semibold text-accent hover:bg-accent/20 disabled:opacity-60"
              title="Add a new lead"
            >
              {leadOpen ? '× Cancel' : '+ Lead'}
            </button>
          </div>
        </FormGroup>
        {leadOpen && (
          <div className="col-span-2 rounded-card border border-accent/40 bg-accent/[0.04] p-3 lg:col-span-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-wide text-accent">
                New Lead
              </div>
              <span className="text-[10px] text-[var(--text-muted)]">
                Will be saved as Party · customerType = Lead
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormGroup label="Contact Name">
                <Input
                  value={lead.contactPerson}
                  onChange={(e) => setLead({ ...lead, contactPerson: e.target.value })}
                  placeholder="Person we spoke to"
                />
              </FormGroup>
              <FormGroup label="Company Name" required error={fe.errors.company}>
                <Input
                  invalid={fe.has('company')}
                  value={lead.companyName}
                  onChange={(e) => { setLead({ ...lead, companyName: e.target.value }); fe.clear('company'); }}
                  placeholder="Business name"
                />
              </FormGroup>
              <FormGroup label="Phone (10 digits)" error={fe.errors.phone}>
                <Input
                  invalid={fe.has('phone')}
                  value={lead.mobile}
                  onChange={(e) => { setLead({ ...lead, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }); fe.clear('phone'); }}
                  placeholder="9876543210"
                  inputMode="numeric"
                />
              </FormGroup>
              <FormGroup label="Email">
                <Input
                  type="email"
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="name@example.com"
                />
              </FormGroup>
              <FormGroup label="Address" className="col-span-2">
                <Textarea
                  rows={2}
                  value={lead.address}
                  onChange={(e) => setLead({ ...lead, address: e.target.value })}
                  placeholder="Street, city, state, PIN"
                />
              </FormGroup>
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button
                variant="glass"
                onClick={() => { setLeadOpen(false); setLead(emptyLead()); }}
                disabled={leadMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onSaveLead}
                disabled={leadMutation.isPending}
              >
                {leadMutation.isPending ? 'Saving…' : 'Save Lead'}
              </Button>
            </div>
          </div>
        )}
        <FormGroup label="Priority">
          <Select
            value={form.priority}
            onChange={(e) => setForm({ ...form, priority: e.target.value as Priority })}
            disabled={!!isReadOnly}
          >
            <option>Low</option>
            <option>Normal</option>
            <option>High</option>
            <option>Urgent</option>
          </Select>
        </FormGroup>
        <FormGroup label="Customer PO Ref">
          <Input
            value={form.customerPoRef}
            onChange={(e) => setForm({ ...form, customerPoRef: e.target.value })}
            placeholder="Optional"
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Sales Rep" required error={fe.errors.salesRep}>
          <Input
            invalid={fe.has('salesRep')}
            value={form.salesRep}
            onChange={(e) => { setForm({ ...form, salesRep: e.target.value }); fe.clear('salesRep'); }}
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Source">
          <Select
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
            disabled={!!isReadOnly}
          >
            <option>Direct</option>
            <option>Trade Fair</option>
            <option>WhatsApp</option>
            <option>Email</option>
            <option>Catalogue</option>
            <option>Referral</option>
          </Select>
        </FormGroup>
        <FormGroup label="Delivery Address" className="col-span-2 lg:col-span-3">
          <Textarea
            rows={2}
            value={form.deliveryAddress}
            onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
            placeholder="Defaults to customer's master address"
            disabled={!!isReadOnly}
          />
        </FormGroup>
        <FormGroup label="Enquiry Note" className="col-span-2 lg:col-span-3">
          <Textarea
            rows={2}
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            disabled={!!isReadOnly}
          />
        </FormGroup>
      </div>

      {selectedParty && (
        <div className="mt-3 rounded-card border border-[var(--border-color)] bg-white/[0.02] p-3 text-xs">
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
            Customer credit snapshot
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span>Limit: <strong>₹{formatINR(Number(selectedParty.creditLimit ?? 0))}</strong></span>
            <span>Outstanding: <strong>₹{formatINR(Number(selectedParty.outstanding))}</strong></span>
            <span>This order: <strong>₹{formatINR(totals.total)}</strong></span>
            <span>Type: <strong>{selectedParty.customerType ?? '—'}</strong></span>
          </div>
        </div>
      )}

      <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
        <input
          type="checkbox"
          checked={form.gstOnRatePc}
          disabled={!!isReadOnly}
          onChange={(e) => setForm({ ...form, gstOnRatePc: e.target.checked })}
          className="h-4 w-4 accent-accent"
        />
        GST Extra on Rate/PC
      </label>

      <ItemsTable<LineState>
        title="Items"
        rows={form.items}
        onChange={(items) => setForm({ ...form, items })}
        newRow={emptyLine}
        readOnly={!!isReadOnly}
        totalLabel="Est. Order Value"
        totalValue={`₹${formatINR(totals.total)}`}
        columns={[
          {
            key: 'item',
            header: 'Item',
            cell: (row, _i, update) => (
              <CategoryItemPicker
                items={catalogue.map((c) => ({
                  id: c.id,
                  code: c.code,
                  name: c.name,
                  category: c.category,
                  currentStock: c.currentStock,
                  stockStatus: c.stockStatus,
                  barcode: c.barcode,
                  color: c.color,
                  size: c.size,
                  brand: c.brand,
                  style: c.style,
                  collection: c.collection,
                }))}
                value={row.itemId ?? '__custom__'}
                disabled={!!isReadOnly}
                showBarcode={false}
                onCustom={() => update({ itemId: null, stockStatus: null })}
                onPick={(id) => {
                  const it = catalogue.find((c) => c.id === id);
                  if (it) {
                    update({
                      itemId: it.id,
                      itemCode: it.code,
                      itemName: it.name,
                      unit: it.unit,
                      hsn: it.hsn ?? '',
                      gstPct: String(it.gstPct),
                      rate: String(it.customerPrice),
                      stockStatus: it.stockStatus,
                      currentStock: it.currentStock,
                    });
                  }
                }}
              />
            ),
          },
          {
            key: 'barcode',
            header: 'SKU Barcode',
            width: '100px',
            cell: (row) => {
              const bc = row.itemId != null ? catalogue.find((c) => c.id === row.itemId)?.barcode ?? '' : '';
              return <span className="text-[11px] text-[var(--text-secondary)]">{bc || '—'}</span>;
            },
          },
          {
            key: 'itemName',
            header: 'Description',
            cell: (row, _i, update) => (
              <Input
                value={row.itemName}
                onChange={(e) => update({ itemName: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'qty',
            header: 'Qty',
            width: '62px',
            align: 'right',
            cell: (row, _i, update) => (
              <Input
                type="number"
                value={row.qty}
                onChange={(e) => update({ qty: e.target.value })}
                disabled={!!isReadOnly}
                className="text-right"
              />
            ),
          },
          {
            key: 'unit',
            header: 'Unit',
            width: '64px',
            cell: (row, _i, update) => (
              <Input
                value={row.unit}
                onChange={(e) => update({ unit: e.target.value })}
                disabled={!!isReadOnly}
              />
            ),
          },
          {
            key: 'rate',
            header: 'Rate (₹)',
            width: '110px',
            align: 'right',
            cell: (row) => (
              <span className="block w-full text-right font-semibold text-accent">
                {row.rate !== '' ? `₹${Number(row.rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
              </span>
            ),
          },
          ...(isCorporate
            ? [
                {
                  key: 'discount',
                  header: 'Discount',
                  width: '118px',
                  align: 'right' as const,
                  cell: (row: LineState, _i: number, update: (patch: Partial<LineState>) => void) => (
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        disabled={!!isReadOnly}
                        onClick={() => update({ discountMode: row.discountMode === '%' ? '₹' : '%' })}
                        className="rounded border border-[var(--border-color)] px-1.5 py-1 text-xs font-bold"
                        title="Toggle percentage / rupee amount"
                      >
                        {row.discountMode}
                      </button>
                      <DiscountInput
                        value={row.discount}
                        disabled={!!isReadOnly}
                        onChange={(v) => update({ discount: v })}
                      />
                    </div>
                  ),
                },
              ]
            : []),
          {
            key: 'ratepc',
            header: 'Rate/pc',
            width: '98px',
            align: 'right',
            cell: (row: LineState, _i: number, update: (patch: Partial<LineState>) => void) => {
              // Per-piece rate after the line discount = net line value ÷ qty.
              const qty = Number(row.qty || 0);
              const rate = Number(row.rate || 0);
              const net = lineNet(row);
              const perPc = qty > 0 ? net / qty : rate;
              const g = Number(row.gstPct || 0);
              // Rate is GST-inclusive. Rate/pc is the ex-GST per-pc exactly as the
              // Sales Order prints it — GST% picked from the HSN GST master by price.
              const showPerPc = gstSplit(net, qty, row.hsn, g, gstBands, gstOnTop).ratePc;
              // Corporate & Projects: editable — entering a per-pc rate back-calculates
              // the line discount in the current % / ₹ mode (inverse of Discount).
              if (isCorporate && !isReadOnly) {
                return (
                  <RatePcInput
                    perPc={perPc}
                    shown={showPerPc}
                    rate={rate}
                    qty={qty}
                    // GST on top: the typed Rate/Pc IS the discounted rate.
                    toInclusive={(ex) => (gstOnTop ? ex : inclusiveFromExRate(ex, row.hsn, g, gstBands))}
                    // A typed rate is kept as an exact ₹ line discount — a % is saved to
                    // 2 decimals only, which billed a few rupees off the typed rate.
                    discountMode="₹"
                    onDiscount={(v) => update({ discountMode: '₹', discount: v })}
                  />
                );
              }
              return (
                <span className="block w-full text-right font-semibold text-accent">
                  {row.rate !== '' ? `₹${showPerPc.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                </span>
              );
            },
          },
          ...(form.gstOnRatePc
            ? [
                {
                  key: 'gstpc',
                  header: 'GST/pc',
                  width: '90px',
                  align: 'right' as const,
                  cell: (row: LineState) => {
                    const qty = Number(row.qty || 0);
                    // Rate is GST-inclusive: the GST inside it per pc, at the HSN
                    // master's rate — as the Sales Order prints it.
                    const s = gstSplit(lineNet(row), qty, row.hsn, Number(row.gstPct || 0), gstBands, gstOnTop);
                    return (
                      <span className="block w-full text-right text-[var(--text-secondary)]">
                        {row.rate !== '' ? (
                          <>
                            ₹{s.gstPerPc.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                            <span className="text-[9px]">({s.gstPct}%)</span>
                          </>
                        ) : '—'}
                      </span>
                    );
                  },
                },
              ]
            : []),
          {
            key: 'amount',
            header: 'Amount',
            width: '120px',
            align: 'right',
            cell: (row) => {
              const gross = Number(row.qty || 0) * Number(row.rate || 0);
              const net = lineNet(row);
              // Rate is GST-inclusive → the discounted net is the amount; with GST
              // on top (Corporate & Projects + tick) the GST is added to it.
              const amt = gstOnTop ? gstSplit(net, Number(row.qty || 0), row.hsn, Number(row.gstPct || 0), gstBands, true).amount : net;
              return (
                <div className="text-right">
                  <span className="font-bold text-accent">₹{formatINR(amt)}</span>
                  {isCorporate && net !== gross && (
                    <div className="text-[10px] text-[var(--text-muted)] line-through">
                      ₹{formatINR(gross)}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            key: 'stock',
            header: 'Stock',
            width: '90px',
            cell: (row) =>
              row.stockStatus ? (
                <span
                  className={
                    row.stockStatus === 'RED'
                      ? 'rounded-full bg-danger/15 px-2 py-0.5 text-[10px] font-bold text-danger'
                      : row.stockStatus === 'AMBER'
                      ? 'rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-bold text-warning'
                      : 'rounded-full bg-success/15 px-2 py-0.5 text-[10px] font-bold text-success'
                  }
                >
                  {row.stockStatus === 'GREEN' ? 'OK' : row.stockStatus === 'AMBER' ? 'Low' : 'Short'}
                </span>
              ) : (
                <span className="text-[var(--text-muted)]">—</span>
              ),
          },
        ]}
      />

      {/* One-time special discount (CD / Final Sale) — applied to the whole order. */}
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2 rounded-md border border-[var(--border-color)] bg-[var(--bg-soft)] px-3 py-2 text-xs">
        <span className="mr-auto text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Special Discount (CD / Final Sale)</span>
        <input
          value={form.specialDiscLabel}
          disabled={!!isReadOnly}
          onChange={(e) => setForm({ ...form, specialDiscLabel: e.target.value })}
          placeholder="Label — e.g. CD"
          className="w-32 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1"
        />
        <select
          value={form.specialDiscType}
          disabled={!!isReadOnly}
          onChange={(e) => setForm({ ...form, specialDiscType: e.target.value as 'amount' | 'pct' })}
          className="rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-1.5 py-1"
        >
          <option value="amount">₹</option>
          <option value="pct">%</option>
        </select>
        <input
          type="number"
          min={0}
          value={form.specialDiscValue}
          disabled={!!isReadOnly}
          onChange={(e) => setForm({ ...form, specialDiscValue: e.target.value })}
          placeholder="0"
          className="w-24 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1 text-right"
        />
      </div>

      {totals.shortLines > 0 && (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning/[0.08] px-3 py-2 text-xs text-warning">
          <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> {totals.shortLines} line(s) have insufficient stock. After saving, use{' '}
          <strong>Order Review → Raise MRN for shortfall</strong> to auto-create procurement MRNs.
        </div>
      )}
    </Modal>
  );
}

function toPayload(f: FormState): CreateEnquiryPayload {
  return {
    enqDate: f.enqDate,
    partyId: f.partyId as number,
    deliveryAddress: f.deliveryAddress.trim() || undefined,
    customerPoRef: f.customerPoRef.trim() || undefined,
    expectedDelivery: f.expectedDelivery || undefined,
    priority: f.priority,
    salesRep: f.salesRep.trim(),
    source: f.source.trim() || undefined,
    remarks: f.remarks.trim() || undefined,
    gstOnRatePc: f.gstOnRatePc,
    specialDiscount: Number(f.specialDiscValue) > 0 ? Number(f.specialDiscValue) : 0,
    specialDiscountType: Number(f.specialDiscValue) > 0 ? f.specialDiscType : undefined,
    specialDiscountLabel: Number(f.specialDiscValue) > 0 ? (f.specialDiscLabel.trim() || undefined) : undefined,
    items: f.items
      .filter((it) => it.itemName.trim() && Number(it.qty) > 0 && it.unit.trim())
      .map((it) => {
        // Custom discount (backend ignores it for non-Corporate customers).
        const dval = Number(it.discount || 0);
        return {
          itemId: it.itemId ?? undefined,
          itemCode: it.itemCode || undefined,
          itemName: it.itemName.trim(),
          hsn: it.hsn || undefined,
          gstPct: it.gstPct ? Number(it.gstPct) : undefined,
          qty: Number(it.qty),
          unit: it.unit.trim(),
          rate: it.rate !== '' ? Number(it.rate) : undefined,
          discountPct: it.discountMode === '%' && dval > 0 ? dval : undefined,
          discountAmt: it.discountMode === '₹' && dval > 0 ? dval : undefined,
        };
      }),
  };
}

/**
 * Editable per-piece rate for Corporate & Projects lines. Holds its own text so
 * typing is smooth; only re-syncs from the derived per-pc when not being edited,
 * and back-calculates the line discount (in the row's % / ₹ mode) on change.
 */
// Focus-guarded discount input: while the user types, its own text is the source
// of truth (never overwritten by a re-render); when not focused it shows the
// stored discount rounded to 2dp — so a per-pc-derived full-precision discount
// still displays cleanly.
function DiscountInput({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(value === '' ? '' : String(r2(Number(value))));
  }, [value, focused]);
  return (
    <Input
      type="number"
      value={text}
      disabled={disabled}
      className="text-right"
      placeholder="0"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        setText(e.target.value);
        onChange(e.target.value);
      }}
    />
  );
}

function RatePcInput({
  perPc,
  shown,
  rate,
  qty,
  toInclusive,
  discountMode,
  onDiscount,
}: {
  /** Discounted GST-inclusive per-pc. */
  perPc: number;
  /** The ex-GST Rate/Pc shown (as the Sales Order prints it). */
  shown: number;
  rate: number;
  qty: number;
  /** Typed ex-GST Rate/Pc → GST-inclusive per-pc (GST% from the HSN master). */
  toInclusive: (exRate: number) => number;
  discountMode: '%' | '₹';
  onDiscount: (value: string) => void;
}) {
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(rate > 0 || perPc ? String(r2(shown)) : '');
  }, [shown, rate, perPc, focused]);
  return (
    <Input
      type="number"
      value={text}
      className="text-right"
      placeholder="0"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        setText(e.target.value);
        const entered = Number(e.target.value || 0);
        if (!(rate > 0)) return;
        // An ex-GST entry maps back to a GST-inclusive per-pc before the discount
        // is derived (rate is GST-inclusive). Full precision so it reconstructs.
        const target = toInclusive(entered);
        const disc = discountMode === '₹' ? (rate - target) * qty : (1 - target / rate) * 100;
        onDiscount(String(Math.max(0, disc)));
      }}
    />
  );
}
